import React from 'react';
import classnames from 'classnames';
import { TextLink, Icon } from '../../index';
import { SocialMediaNavigation, SocialMediaNavigationItem } from './social-media-links';
import { ButtonType, LinkType } from '../types';
import styles from './footer.module.scss';
import { splitMultilineText } from '../../utils/split-multiline-text';
import { withSetting } from '../../utils';

export type LanguageSwitchType = {
  options: {
    label: string;
    value: string;
  }[];
  value: string;
  onChange: React.InputHTMLAttributes<HTMLSelectElement>['onChange'];
  label: string;
};

export type FooterType = {
  logoLink: LinkType;
  logoSrc: string;
  cta?: LinkType;
  linkGroup1?: LinkType[];
  linkGroup2?: LinkType[];
  linkGroup3?: LinkType[];
  copyText?: string;
  languageSwitch?: LanguageSwitchType;
  countryLink?: LinkType & { ariaLabel: string };
  className?: string;
  socialMediaNavigationLabel: string;
  socialMediaNavigationItems: SocialMediaNavigationItem[];
  cookieConsentButton?: ButtonType;
};

const baseClass = 'footer';

const renderLinkList = (
  variant: 'primary' | 'secondary' | 'tertiary',
  list: LinkType[],
  cookieConsentButton?: ButtonType
) => (
  <ul className={classnames(styles['navigation__list'], styles[`navigation__list--${variant}`])}>
    {list.map((link, index) => (
      <li key={link.label + index} className={styles['navigation__list__item']}>
        <TextLink {...link} variant="hyperlink-100" className={styles['navigation__link']} linkText={link.label} />
      </li>
    ))}
    {cookieConsentButton && variant === 'tertiary' && (
      <li className={styles['navigation__list__item']}>
        <button onClick={cookieConsentButton.onClick} className={styles.consentButton}>
          {cookieConsentButton.label}
        </button>
      </li>
    )}
  </ul>
);

const FooterComponent: React.FC<FooterType> = ({
  logoLink,
  logoSrc,
  cta,
  linkGroup1,
  linkGroup2,
  linkGroup3,
  socialMediaNavigationLabel,
  socialMediaNavigationItems,
  countryLink,
  languageSwitch,
  copyText,
  className,
  cookieConsentButton
}) => {
  const classes = classnames(styles[baseClass], className);

  return (
    <footer className={classes} data-nosnippet="true">
      <div className={styles['upper-content']}>
        <div className={styles[`${baseClass}__logo`]}>
          <a
            href={logoLink.href}
            rel={logoLink.rel}
            onClick={logoLink.onClick}
            target={logoLink.target}
            aria-label={logoLink.label}
          >
            <img src={logoSrc} alt={logoLink.label} loading="lazy" height="95" width="70" />
          </a>
        </div>
        {(linkGroup1 || linkGroup2 || linkGroup3) && (
          <nav className={styles['navigation']}>
            {linkGroup1 && renderLinkList('primary', linkGroup1)}
            {(linkGroup2 || linkGroup3) && (
              <div className={styles['secondary-tertiary']}>
                {linkGroup2 && renderLinkList('secondary', linkGroup2)}
                {linkGroup3 && renderLinkList('tertiary', linkGroup3, cookieConsentButton)}
              </div>
            )}
          </nav>
        )}
        <div className={styles[`${baseClass}__cta-wrapper`]}>
          {cta && (
            <TextLink
              {...cta}
              variant="button-secondary"
              icon={{ name: 'arrow-right--medium', 'aria-hidden': true }}
              iconPosition="right"
              size="300"
              className={styles[`${baseClass}__cta`]}
              linkText={cta.label}
            />
          )}
        </div>
      </div>
      <div className={styles[`${baseClass}__switches-and-socialnav-wrapper`]}>
        <div className={styles['language-country']}>
          {countryLink && (
            <div className={styles['country-link']}>
              <Icon name="dealer-locator-location--small" aria-hidden className={styles['country-link__icon']} />
              <a
                href={countryLink.href}
                onClick={countryLink.onClick}
                className={styles[`country-link__anchor`]}
                aria-label={countryLink.ariaLabel}
              >
                {countryLink.label}
              </a>
            </div>
          )}
          {languageSwitch && (
            <div className={styles['language-switch']}>
              <select
                className={styles['language-switch__select']}
                aria-label={languageSwitch.label}
                onChange={languageSwitch.onChange}
                value={languageSwitch.value}
              >
                {languageSwitch.options.map((option, i) => (
                  <option value={option.value} key={option.value + i} className={styles['language-switch__option']}>
                    {option.label}
                  </option>
                ))}
              </select>
              <Icon name="chevron-down--small" aria-hidden className={styles['language-switch__icon']} />
            </div>
          )}
        </div>
        <SocialMediaNavigation ariaLabel={socialMediaNavigationLabel} items={socialMediaNavigationItems} />
      </div>
      {copyText && <div>{splitMultilineText(copyText, 'caption-200-fixed', styles['copy-text--paragraph'])}</div>}
    </footer>
  );
};

export const Footer = withSetting(FooterComponent, 'inverted');
